import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { KatRow, KatColumn, KatButton } from '@amzn/katal-react';
import { makeAwsSignedRequest } from '../services/ApiService';
import { formatUTC, parseGeneratedAt, prettyDisplayTransform, truncateText } from '../utils/commonUtils';
import { useFilterState } from '../hooks/useFilterState';
import DateRangePicker from './DateRangePicker';
import MultiSelectDropdown from './MultiSelectDropdown';
import DiagnosticAiQuestion from './DiagnosticAiQuestion';
import PSTriageAssistButton from './PSTriageAssistButton';
import FilterHeader from './common/FilterHeader';
import { LoadingIndicator, ErrorIndicator, EmptyState, InitialState } from './common/LoadingStates';
import { AiAssistDiagnosticResponse } from '../services/Ai/AiAssistService';
import './DiagnosticView.scss';

/* ===================== Types ===================== */
interface AggregatedDiagnosticRow {
    diagnosticCode: string;
    diagnosticDescription: string;
    clients: string[];
    processors: string[];
    sampleRefundIds: string[];
    count: number;
    bucketKey: string;
    snapshotGeneratedAt: string;
    pstriageJob?: AiAssistDiagnosticResponse | null;
}

interface DiagnosticViewProps {
    stage: string;
    realm: string;
    triggerFetch?: boolean;
    filtersFromTracker?: {
        startDateTime: Date;
        endDateTime: Date;
        selectedClients: string[];
        autoExecute: boolean;
        timestamp: number;
    } | null;
    onFiltersApplied?: () => void;
    /** Raw tracker generatedAt string so we can display the exact same UTC "Last Refreshed" */
    trackerGeneratedAt?: string | null;
}

/* ===================== Helpers ===================== */

/** Multiline comma-list with expand/hide toggle (for Clients/Processors) */
const CommaList: React.FC<{
    items: string[];
    expanded: boolean;
    onToggle?: () => void;
    maxCollapsed?: number;
    expandable?: boolean;
    className?: string;
}> = ({ items, expanded, onToggle, maxCollapsed = 3, expandable = true, className }) => {
    const visible = expanded ? items : items.slice(0, maxCollapsed);
    const remaining = Math.max(0, items.length - maxCollapsed);

    return (
        <div className={`comma-list ${className || ''}`}>
            {visible.map((val, i) => (
                <div key={`${val}-${i}`} className="comma-item">
                    <span className="text">{val}</span>
                    <span className="comma">{i < visible.length - 1 ? ',' : ''}</span>
                </div>
            ))}
            {!expanded && expandable && remaining > 0 && (
                <div className="comma-item toggle-row">
                    <button type="button" className="inline-toggle more" onClick={onToggle}>
                        ... more ({remaining})
                    </button>
                </div>
            )}
            {expanded && expandable && items.length > maxCollapsed && (
                <div className="comma-item toggle-row">
                    <button type="button" className="inline-toggle hide" onClick={onToggle}>
                        hide
                    </button>
                </div>
            )}
        </div>
    );
};

/* ===================== Component ===================== */
const DiagnosticView: React.FC<DiagnosticViewProps> = ({
                                                           stage,
                                                           realm,
                                                           triggerFetch = false,
                                                           filtersFromTracker = null,
                                                           onFiltersApplied,
                                                           trackerGeneratedAt = null,
                                                       }) => {
    const isMountedRef = useRef(true);
    const processingTrackerFiltersRef = useRef(false);

    // Use filter hook
    const { filters, filterOptions, updateFilter, updateFilterOptions, setFilters } = useFilterState({
        startDateTime: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000),
        endDateTime: new Date(),
        selectedClients: ['AllClients'],
        selectedDiagnosticCodes: ['AllDiagnosticCodes'],
        selectedProcessors: ['AllProcessors']
    });

    // Data
    const [aggregatedData, setAggregatedData] = useState<AggregatedDiagnosticRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Per-row expansions (refund IDs + client/processor lists)
    const [expandedRefundPreview, setExpandedRefundPreview] = useState<Map<number, Set<number>>>(new Map());
    const [expandedClientsRows, setExpandedClientsRows] = useState<Set<number>>(new Set());
    const [expandedProcessorsRows, setExpandedProcessorsRows] = useState<Set<number>>(new Set());

    const toggleRefundPreview = (rowIdx: number, previewIdx: number) =>
        setExpandedRefundPreview(prev => {
            const next = new Map(prev);
            const set = new Set(next.get(rowIdx) ?? []);
            set.has(previewIdx) ? set.delete(previewIdx) : set.add(previewIdx);
            next.set(rowIdx, set);
            return next;
        });

    const toggleClientsRow = (idx: number) =>
        setExpandedClientsRows(prev => {
            const next = new Set(prev);
            next.has(idx) ? next.delete(idx) : next.add(idx);
            return next;
        });

    const toggleProcessorsRow = (idx: number) =>
        setExpandedProcessorsRows(prev => {
            const next = new Set(prev);
            next.has(idx) ? next.delete(idx) : next.add(idx);
            return next;
        });

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const fetchDiagnosticData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                startTime: filters.startDateTime!.toISOString(),
                endTime: filters.endDateTime!.toISOString(),
            });

            if (filters.selectedClients!.length > 0 && !filters.selectedClients!.includes('AllClients')) {
                params.set('clientId', filters.selectedClients!.join(','));
            }
            if (filters.selectedDiagnosticCodes!.length > 0 && !filters.selectedDiagnosticCodes!.includes('AllDiagnosticCodes')) {
                params.set('diagnosticCode', filters.selectedDiagnosticCodes!.join(','));
            }
            if (filters.selectedProcessors!.length > 0 && !filters.selectedProcessors!.includes('AllProcessors')) {
                params.set('processor', filters.selectedProcessors!.join(','));
            }

            const endpoint = `diagnostic?${params.toString()}`;
            const data = await makeAwsSignedRequest(stage, realm, endpoint);

            if (data.filterOptions) {
                updateFilterOptions({
                    availableClients: ['AllClients', ...(data.filterOptions.clientIds || [])],
                    availableDiagnosticCodes: ['AllDiagnosticCodes', ...(data.filterOptions.diagnosticCodes || [])],
                    availableProcessors: ['AllProcessors', ...(data.filterOptions.processors || [])]
                });
            }
            setAggregatedData(data.aggregatedData || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch diagnostic data');
            setAggregatedData([]);
        } finally {
            setLoading(false);
        }
    }, [stage, realm, filters.startDateTime, filters.endDateTime, filters.selectedClients, filters.selectedDiagnosticCodes, filters.selectedProcessors, updateFilterOptions]);

    // Apply filters passed from tracker
    useEffect(() => {
        if (!filtersFromTracker || !filtersFromTracker.autoExecute || processingTrackerFiltersRef.current) return;
        processingTrackerFiltersRef.current = true;

        setFilters({
            ...filters,
            startDateTime: filtersFromTracker.startDateTime,
            endDateTime: filtersFromTracker.endDateTime,
            selectedClients: filtersFromTracker.selectedClients,
            selectedDiagnosticCodes: ['AllDiagnosticCodes'],
            selectedProcessors: ['AllProcessors']
        });

        setTimeout(() => {
            if (isMountedRef.current) {
                fetchDiagnosticData().finally(() => {
                    processingTrackerFiltersRef.current = false;
                    onFiltersApplied?.();
                });
            }
        }, 300);
    }, [filtersFromTracker?.autoExecute, filtersFromTracker?.timestamp, fetchDiagnosticData, onFiltersApplied, setFilters, filters]);

    // Triggered fetch from parent
    useEffect(() => {
        if (triggerFetch) fetchDiagnosticData();
    }, [triggerFetch, fetchDiagnosticData]);

    const handleSearch = () => {
        setExpandedRefundPreview(new Map());
        setExpandedClientsRows(new Set());
        setExpandedProcessorsRows(new Set());
        fetchDiagnosticData();
    };

    // Last Refreshed — mirror Tracker’s last refresh time (UTC)
    const parsedTrackerGeneratedAt = useMemo(() => parseGeneratedAt(trackerGeneratedAt), [trackerGeneratedAt]);
    const lastRefreshedText = useMemo(() => {
        if (parsedTrackerGeneratedAt) {
            return `${formatUTC(parsedTrackerGeneratedAt)} (UTC)`;
        }
        return 'Not yet loaded';
    }, [parsedTrackerGeneratedAt]);

    return (
        <div className="diagnostic-view">
            {/* Filters */}
            <div className="diagnostic-filters-section">
                <FilterHeader 
                    lastRefreshed={lastRefreshedText}
                    error={error}
                />

                <KatRow noGutters className="filters-row">
                    <KatColumn md={6} sm={12} xs={12}>
                        <div className="filter-item">
                            <DateRangePicker
                                startDateTime={filters.startDateTime!}
                                endDateTime={filters.endDateTime!}
                                onStartDateTimeChange={(date) => updateFilter('startDateTime', date)}
                                onEndDateTimeChange={(date) => updateFilter('endDateTime', date)}
                                maxDate={new Date()}
                                timezoneLabel="UTC"
                            />
                        </div>
                    </KatColumn>
                </KatRow>

                <KatRow noGutters className="filters-row">
                    <KatColumn md={4} sm={6} xs={12}>
                        <div className="filter-item">
                            <MultiSelectDropdown
                                label="Client IDs"
                                placeholder="Select client IDs..."
                                options={filterOptions.availableClients}
                                selectedOptions={filters.selectedClients!}
                                onSelectionChange={(clients) => updateFilter('selectedClients', clients)}
                                loading={loading}
                                allOption="AllClients"
                                enforceExclusiveAll
                                displayTransform={prettyDisplayTransform}
                            />
                        </div>
                    </KatColumn>

                    <KatColumn md={4} sm={6} xs={12}>
                        <div className="filter-item">
                            <MultiSelectDropdown
                                label="Diagnostic Codes"
                                placeholder="Select diagnostic codes..."
                                options={filterOptions.availableDiagnosticCodes!}
                                selectedOptions={filters.selectedDiagnosticCodes!}
                                onSelectionChange={(codes) => updateFilter('selectedDiagnosticCodes', codes)}
                                loading={loading}
                                allOption="AllDiagnosticCodes"
                                enforceExclusiveAll
                                displayTransform={prettyDisplayTransform}
                            />
                        </div>
                    </KatColumn>

                    <KatColumn md={4} sm={6} xs={12}>
                        <div className="filter-item">
                            <MultiSelectDropdown
                                label="Processors"
                                placeholder="Select processors..."
                                options={filterOptions.availableProcessors!}
                                selectedOptions={filters.selectedProcessors!}
                                onSelectionChange={(processors) => updateFilter('selectedProcessors', processors)}
                                loading={loading}
                                allOption="AllProcessors"
                                enforceExclusiveAll
                                displayTransform={prettyDisplayTransform}
                            />
                        </div>
                    </KatColumn>
                </KatRow>

                <KatRow noGutters className="filters-row">
                    <KatColumn xs={12}>
                        <div className="filter-actions">
                            <KatButton
                                variant="primary"
                                label={loading ? 'Loading...' : 'Search'}
                                onClick={handleSearch}
                                disabled={loading}
                            />
                        </div>
                    </KatColumn>
                </KatRow>
            </div>

            <DiagnosticAiQuestion
                stage={stage}
                realm={realm}
                startDateTime={filters.startDateTime!}
                endDateTime={filters.endDateTime!}
                selectedClients={filters.selectedClients!}
                selectedProcessors={filters.selectedProcessors!}
            />

            {/* Results */}
            <div className="diagnostic-results-section">
                {loading && <LoadingIndicator message="Loading diagnostic data..." />}

                {!loading && error && <ErrorIndicator error={error} />}

                {!loading && !error && aggregatedData.length === 0 && (
                    <InitialState 
                        title="Diagnostic Analysis"
                        message="Apply filters above to search for diagnostic refund data"
                    />
                )}

                {!loading && !error && aggregatedData.length > 0 && (
                    <div className="diagnostic-table">
                        <div className="table-header">
                            <div className="table-row">
                                <div className="table-cell">Client</div>
                                <div className="table-cell">Processor</div>                               
                                <div className="table-cell">Diagnostic Code</div>
                                <div className="table-cell">Diagnostic Description</div>
                                <div className="table-cell">Sample Refund IDs</div>
                                <div className="table-cell">Count</div>
                                <div className="table-cell">PSTriage</div>
                            </div>
                        </div>

                        <div className="table-body">
                            {aggregatedData.length === 0 ? (
                                <EmptyState 
                                    title=""
                                    message="No diagnostic data found for the selected filters."
                                />
                            ) : (
                                aggregatedData.map((row, index) => {
                                    const clientsExpanded = expandedClientsRows.has(index);
                                    const processorsExpanded = expandedProcessorsRows.has(index);
                                    const previewRefunds = row.sampleRefundIds.slice(0, 3);
                                    const hasMoreRefunds = row.sampleRefundIds.length > 3;

                                    return (
                                        <div key={index} className="table-row">
                                            <div className="table-cell">
                                                <div className="cell-content">
                                                    <CommaList
                                                        items={row.clients}
                                                        expanded={clientsExpanded}
                                                        onToggle={() => toggleClientsRow(index)}
                                                        maxCollapsed={3}
                                                        expandable
                                                        className="clients-list"
                                                    />
                                                </div>
                                            </div>

                                            <div className="table-cell">
                                                <div className="cell-content">
                                                    <CommaList
                                                        items={row.processors}
                                                        expanded={processorsExpanded}
                                                        onToggle={() => toggleProcessorsRow(index)}
                                                        maxCollapsed={3}
                                                        expandable
                                                        className="processors-list"
                                                    />
                                                </div>
                                            </div>

                                            <div className="table-cell">
                                                <div className="cell-content diagnostic-code">{row.diagnosticCode}</div>
                                            </div>

                                            <div className="table-cell">
                                                <div className="cell-content diagnostic-description">
                                                    {row.diagnosticDescription}
                                                </div>
                                            </div>

                                            <div className="table-cell">
                                                <div className="cell-content refund-ids">
                                                    {previewRefunds.map((id, i) => {
                                                        const expanded = expandedRefundPreview.get(index)?.has(i) ?? false;
                                                        const isTruncated = id.length > 28;
                                                        const display = expanded ? id : truncateText(id, 28);
                                                        return (
                                                            <div key={i} className={`refund-id ${expanded ? 'expanded' : 'collapsed'}`}>
                                                                <span className="id-text">{display}</span>
                                                                {isTruncated && (
                                                                    <button
                                                                        type="button"
                                                                        className="inline-toggle mini"
                                                                        onClick={() => toggleRefundPreview(index, i)}
                                                                    >
                                                                        {expanded ? 'hide' : 'expand'}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                    {hasMoreRefunds && <div className="ids-ellipsis">…</div>}
                                                </div>
                                            </div>

                                            <div className="table-cell">
                                                <div className="cell-content count">
                                                    <strong>{row.count}</strong>
                                                </div>
                                            </div>

                                            <div className="table-cell">
                                                <div className="cell-content assist-action">
                                                    <PSTriageAssistButton
                                                        diagnosticCode={row.diagnosticCode}
                                                        diagnosticDescription={row.diagnosticDescription}
                                                        sampleRefundIds={row.sampleRefundIds}
                                                        count={row.count}
                                                        job={row.pstriageJob}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DiagnosticView;
